// src/components/ScadenzePersonaleWidget.js
// Scadenze contrattuali del personale — periodo di prova in scadenza, contratti a termine in
// scadenza, anzianità di servizio maturata. Sono adempimenti veri, con conseguenze legali e
// contrattuali se ci si accorge in ritardo: un periodo di prova che scade senza che nessuno
// se ne accorga diventa un contratto confermato, un contratto a termine dimenticato va
// rinnovato o chiuso entro i termini.
//
// Non chiede di inserire nulla di nuovo: usa la data di assunzione, già obbligatoria in
// anagrafica, più due campi facoltativi (fine periodo prova, scadenza contratto) che chi
// gestisce il personale compila una volta sola quando assume.
//
// Dati sensibili sulle persone: si vede solo con il permesso "scadenze-personale", che di
// default hanno titolare e amministrazione. Spegnibile dalle Impostazioni Aziendali.
import React, { useEffect, useState, useMemo } from "react";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { FileText, ChevronRight } from "lucide-react";

// Anticipi scelti per dare tempo di agire, non per avvisare quando è già tardi:
// il periodo di prova va valutato e comunicato prima della scadenza; un contratto a termine
// richiede più tempo (decisione, eventuale rinnovo, comunicazioni obbligatorie).
const ANTICIPO_PROVA = 30;
const ANTICIPO_CONTRATTO = 60;

function giorniA(dataStr) {
  if (!dataStr) return null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const d = new Date(dataStr+"T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.round((d - oggi)/(1000*60*60*24));
}

function fmtData(dataStr) {
  const d = new Date(dataStr+"T00:00:00");
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"});
}

// Anni interi di servizio compiuti oggi — usati per segnalare il maturare di uno scatto di
// anzianità. Solo il giorno esatto in cui si compiono, e solo da 1 anno in su.
function anniCompiutiOggi(dataAssunzione) {
  if (!dataAssunzione) return null;
  const d = new Date(dataAssunzione+"T00:00:00");
  if (isNaN(d.getTime())) return null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  if (d.getDate() !== oggi.getDate() || d.getMonth() !== oggi.getMonth()) return null;
  const anni = oggi.getFullYear() - d.getFullYear();
  return anni >= 1 ? anni : null;
}

// Calcolo degli avvisi — esportato perché serve anche a chi vuole solo il conteggio
// (il numero accanto al tab in Dipendenti, il richiamo nello Scadenzario) senza montare
// tutto il pannello: una sola logica, mai due che possono divergere.
export function calcolaAvvisiPersonale(dipendenti) {
  const out = [];
  (dipendenti||[]).forEach(d => {
    const nome = `${d.nome||""} ${d.cognome||""}`.trim() || "Dipendente";

    const gProva = giorniA(d.finePeriodoProva);
    if (gProva !== null && gProva >= 0 && gProva <= ANTICIPO_PROVA) {
      out.push({ tipo:"prova", nome, giorni:gProva, data:d.finePeriodoProva,
        testo: gProva===0 ? "Il periodo di prova termina oggi" : `Il periodo di prova termina tra ${gProva} giorn${gProva===1?"o":"i"}`,
        nota:"Se non viene comunicato nulla entro la scadenza, il rapporto si intende confermato." });
    }

    const gContratto = giorniA(d.scadenzaContratto);
    if (gContratto !== null && gContratto >= 0 && gContratto <= ANTICIPO_CONTRATTO) {
      out.push({ tipo:"contratto", nome, giorni:gContratto, data:d.scadenzaContratto,
        testo: gContratto===0 ? "Il contratto a termine scade oggi" : `Il contratto a termine scade tra ${gContratto} giorn${gContratto===1?"o":"i"}`,
        nota: d.tipoContratto ? `Contratto: ${d.tipoContratto}` : "" });
    }

    const anni = anniCompiutiOggi(d.dataAssunzione);
    if (anni) {
      out.push({ tipo:"anzianita", nome, giorni:0, data:d.dataAssunzione,
        testo: `Compie oggi ${anni} ann${anni===1?"o":"i"} di servizio`,
        nota:"Verificare se maturano scatti di anzianità previsti dal CCNL applicato." });
    }
  });
  return out.sort((a,b)=>a.giorni-b.giorni);
}

// Dati che mancano — separati dagli avvisi di scadenza perché rispondono a una domanda
// diversa: non "cosa scade", ma "di chi non so nulla". Senza questo, un elenco vuoto di
// scadenze sembrerebbe dire che va tutto bene, quando invece potrebbe voler dire soltanto
// che nessuno ha mai compilato le date — un silenzio che inganna.
// Vengono segnalate solo le assenze che hanno davvero senso: la data fine prova interessa
// per chi è stato assunto di recente, la scadenza contratto solo per i contratti a termine.
const MESI_RILEVANZA_PROVA = 8; // oltre, il periodo di prova è comunque concluso da tempo
const CONTRATTI_A_TERMINE = ["Tempo determinato","Apprendistato","A progetto","Somministrazione","Stage/Tirocinio","Intermittente"];

export function calcolaDatiMancantiPersonale(dipendenti) {
  const senzaProva = [], senzaScadenza = [], senzaAssunzione = [];
  (dipendenti||[]).forEach(d => {
    const nome = `${d.nome||""} ${d.cognome||""}`.trim() || "Dipendente";
    if (!d.dataAssunzione) { senzaAssunzione.push(nome); return; }

    const gDaAssunzione = giorniA(d.dataAssunzione);
    const mesiDaAssunzione = gDaAssunzione === null ? null : Math.abs(gDaAssunzione) / 30.4;
    if (!d.finePeriodoProva && mesiDaAssunzione !== null && mesiDaAssunzione <= MESI_RILEVANZA_PROVA) {
      senzaProva.push(nome);
    }
    if (!d.scadenzaContratto && CONTRATTI_A_TERMINE.includes(d.tipoContratto)) {
      senzaScadenza.push({ nome, contratto: d.tipoContratto });
    }
  });
  return { senzaProva, senzaScadenza, senzaAssunzione };
}

export default function ScadenzePersonaleWidget({ onNavigate, vistaEstesa }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const [dipendenti, setDipendenti] = useState([]);
  const [attivo, setAttivo] = useState(true);

  const puoVedere = isOrgAdmin || isPlatformOwner || canSee("scadenze-personale");

  useEffect(() => {
    if (!effectiveOrgId || !puoVedere) return;
    return onSnapshot(query(collection(db,"dipendenti"), where("organizationId","==",effectiveOrgId)),
      snap => setDipendenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [effectiveOrgId, puoVedere]);

  // Interruttore aziendale — acceso di default, spegnibile dalle Impostazioni Aziendali.
  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(snap => {
      if (snap.exists() && snap.data()?.scadenzePersonaleAttive === false) setAttivo(false);
    }).catch(()=>{});
  }, [effectiveOrgId]);

  const avvisi = useMemo(() => calcolaAvvisiPersonale(dipendenti), [dipendenti]);
  const mancanti = useMemo(() => calcolaDatiMancantiPersonale(dipendenti), [dipendenti]);

  if (!puoVedere || !attivo) return null;
  if (avvisi.length === 0 && !vistaEstesa) return null;

  const STILI = {
    prova:     { bordo:"#fca5a5", sfondo:"#fef2f2", colore:"#991b1b", etichetta:"PERIODO DI PROVA" },
    contratto: { bordo:"#fde68a", sfondo:"#fffbeb", colore:"#92400e", etichetta:"CONTRATTO A TERMINE" },
    anzianita: { bordo:"#bfdbfe", sfondo:"#eff6ff", colore:"#1e40af", etichetta:"ANZIANITÀ DI SERVIZIO" },
  };

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:16,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13.5,fontWeight:800,color:"#1e1b4b"}}>
          <FileText size={16} color="#7c3aed"/> Scadenze contrattuali del personale
        </div>
        {!vistaEstesa && (
          <button onClick={()=>onNavigate&&onNavigate({page:"dipendenti"})}
            style={{display:"flex",alignItems:"center",gap:3,fontSize:11.5,fontWeight:700,color:"#6d28d9",background:"none",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Apri Dipendenti <ChevronRight size={13}/>
          </button>
        )}
      </div>
      {vistaEstesa && (
        <div style={{fontSize:11.5,color:"#6b7280",marginBottom:12,lineHeight:1.55}}>
          Calcolate dalle date in anagrafica: periodo di prova (avviso {ANTICIPO_PROVA} giorni prima), contratto a termine (avviso {ANTICIPO_CONTRATTO} giorni prima) e anni di servizio compiuti. I campi «Fine periodo prova» e «Scadenza contratto» sono facoltativi: si compilano nell'elenco persone.
        </div>
      )}
      {avvisi.length === 0 ? (
        <div style={{textAlign:"center",padding:24,color:"#9ca3af",fontSize:12.5}}>
          Nessuna scadenza contrattuale in arrivo{vistaEstesa && (mancanti.senzaProva.length>0 || mancanti.senzaScadenza.length>0 || mancanti.senzaAssunzione.length>0) ? " tra le persone con le date compilate." : "."}
        </div>
      ) : avvisi.map((a,i) => {
        const s = STILI[a.tipo];
        return (
          <div key={i} style={{background:s.sfondo,border:`1px solid ${s.bordo}`,borderRadius:9,padding:"9px 13px",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:2}}>
              <span style={{fontSize:9.5,fontWeight:800,color:s.colore,letterSpacing:.3}}>{s.etichetta}</span>
              <span style={{fontSize:10.5,color:"#9ca3af"}}>{fmtData(a.data)}</span>
            </div>
            <div style={{fontSize:12.5,color:"#111827"}}>
              <strong>{a.nome}</strong> — {a.testo}
            </div>
            {a.nota && <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{a.nota}</div>}
          </div>
        );
      })}

      {/* Dati mancanti — solo nel tab completo: risponde a "di chi non so nulla", che è una
          domanda diversa da "cosa scade", e non deve mai essere confusa con un avviso urgente. */}
      {vistaEstesa && (mancanti.senzaProva.length>0 || mancanti.senzaScadenza.length>0 || mancanti.senzaAssunzione.length>0) && (
        <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid #f3f4f6"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#6b7280",marginBottom:8}}>Date non compilate</div>
          <div style={{fontSize:11,color:"#9ca3af",marginBottom:10,lineHeight:1.55}}>
            Non è un problema in sé — ma finché mancano, Nexiora non può avvisarti su queste persone.
          </div>
          {mancanti.senzaAssunzione.length>0 && (
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:9,padding:"9px 13px",marginBottom:6,fontSize:12,color:"#374151"}}>
              <strong>{mancanti.senzaAssunzione.length} senza data di assunzione</strong> — {mancanti.senzaAssunzione.join(", ")}
              <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>Senza questa data non si calcola né l'anzianità di servizio né la rilevanza del periodo di prova.</div>
            </div>
          )}
          {mancanti.senzaProva.length>0 && (
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:9,padding:"9px 13px",marginBottom:6,fontSize:12,color:"#374151"}}>
              <strong>{mancanti.senzaProva.length} assunt{mancanti.senzaProva.length===1?"o":"i"} di recente senza data fine periodo di prova</strong> — {mancanti.senzaProva.join(", ")}
              <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>Se il periodo di prova è previsto dal contratto, compilarla permette a Nexiora di avvisarti prima della scadenza.</div>
            </div>
          )}
          {mancanti.senzaScadenza.length>0 && (
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:9,padding:"9px 13px",marginBottom:6,fontSize:12,color:"#374151"}}>
              <strong>{mancanti.senzaScadenza.length} contratt{mancanti.senzaScadenza.length===1?"o":"i"} a termine senza data di scadenza</strong> — {mancanti.senzaScadenza.map(x=>`${x.nome} (${x.contratto})`).join(", ")}
              <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>Sono contratti che per natura hanno una fine: senza la data, nessuno verrà avvisato prima che arrivi.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Richiamo compatto per lo Scadenzario — una riga sola che dice quante scadenze contrattuali
// ci sono e porta dove si gestiscono davvero (Dipendenti). Il contenuto vero sta in un posto
// solo: qui non si duplica nulla, si segnala e si accompagna.
export function RichiamoScadenzeContrattuali({ onNavigate }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const [dipendenti, setDipendenti] = useState([]);
  const [attivo, setAttivo] = useState(true);

  const puoVedere = isOrgAdmin || isPlatformOwner || canSee("scadenze-personale");

  useEffect(() => {
    if (!effectiveOrgId || !puoVedere) return;
    return onSnapshot(query(collection(db,"dipendenti"), where("organizationId","==",effectiveOrgId)),
      snap => setDipendenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [effectiveOrgId, puoVedere]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(snap => {
      if (snap.exists() && snap.data()?.scadenzePersonaleAttive === false) setAttivo(false);
    }).catch(()=>{});
  }, [effectiveOrgId]);

  const n = useMemo(()=>calcolaAvvisiPersonale(dipendenti).length, [dipendenti]);

  if (!puoVedere || !attivo || n === 0) return null;

  return (
    <div onClick={()=>onNavigate&&onNavigate({page:"dipendenti"})}
      style={{display:"flex",alignItems:"center",gap:10,background:"#faf5ff",border:"1px solid #e9d5ff",
        borderRadius:10,padding:"10px 14px",marginBottom:14,cursor:"pointer"}}>
      <FileText size={15} color="#7c3aed" style={{flexShrink:0}}/>
      <div style={{flex:1,fontSize:12.5,color:"#5b21b6"}}>
        <strong>{n} scadenz{n===1?"a":"e"} contrattual{n===1?"e":"i"} del personale</strong> — periodi di prova, contratti a termine, anzianità di servizio
      </div>
      <span style={{display:"flex",alignItems:"center",gap:3,fontSize:11.5,fontWeight:700,color:"#6d28d9",whiteSpace:"nowrap"}}>
        Vedi in Dipendenti <ChevronRight size={13}/>
      </span>
    </div>
  );
}
